import React from "react";
import { Modal } from "react-bootstrap";
import Button from "components/Button";
import EmployerTermsAgreement from "pages/signUp/employer/EmployerTermsAgreement";
import EmployerTermsOrder from "pages/signUp/employer/EmployerTermsOrder";
import EmployerPartnerProgramAgreement from "./employer/EmployerPartnerProgramAgreement";
import IconTerms from "../../components/terms/IconTerms";
import ApexTerms from "../../components/terms/ApexTerms";
import VirtualAccountNumberDisclosure from "../../components/terms/VirtualAccountNumberDisclosure";

import IRATerms from "../../components/terms/IRATerms";
import EmployerIsolvedTermsOrder from "pages/signUp/employer/EmployerIsolvedTermsOrder";
import ApexTermsRoth from "components/terms/ApexTermsRoth";
import RolloverTerms from "components/terms/RolloverTerms";
import EmployerTermsOrderV2 from "pages/signUp/employer/EmployerTermsOrderV2";
import EvolveTerms from "components/terms/EvolveTerms";
import PassportAgreementSila from "components/terms/PassportAgreementSila";
import CRBAgreement from "components/terms/CRBAgreement";

interface TermsModalProps {
  show?: boolean;
  type?: string;
  termsInfo?: object;
  onClose: () => void;
}

const TermsModal = ({ show, onClose, type, termsInfo }: TermsModalProps) => {
  var content: React.ReactNode;
  var title: React.ReactNode;
  switch (type) {
    case "termsOfService":
      content = <IconTerms />;
      title = (
        <div>
          ICON FINANCIAL SERVICES LLC DISCRETIONARY INVESTMENT ADVISORY CONTRACT
        </div>
      );
      break;
    case "accountAgreement":
      content = (
        // @ts-expect-error info is undeclared by ApexTerms; passed as in the original
        <ApexTerms info={termsInfo} />
      );
      title = (
        <div className="agreements-title">
          <p className="agreements-modal-header">
            Traditional, Rollover, Beneficiary Traditional, or SEP IRA Agreement
          </p>
          <p className="agreements-modal-subheader">
            Select exactly one type of IRA: Traditional IRA
          </p>
        </div>
      );
      break;
    case "accountAgreementRoth":
      content = (
        // @ts-expect-error info is undeclared by ApexTermsRoth; passed as in the original
        <ApexTermsRoth info={termsInfo} />
      );
      title = (
        <div className="agreements-title">
          <p className="agreements-modal-header">Roth IRA Account Agreement</p>
          <p className="agreements-modal-subheader">
            Select exactly one type of IRA: Roth IRA
          </p>
        </div>
      );
      break;
    case "rolloverTerms":
      content = (
        // @ts-expect-error info is undeclared by RolloverTerms; passed as in the original
        <RolloverTerms info={termsInfo} />
      );
      title = (
        <div className="agreements-title">
          <p className="agreements-modal-header">Rollover Account Agreement</p>
          <p className="agreements-modal-subheader">
            Please read important information about your rollover.
          </p>
        </div>
      );
      break;
    case "evolveAgreement":
      content = (
        // @ts-expect-error EvolveTerms narrows info to { effectiveDate: string }; passed as in the original
        <EvolveTerms info={termsInfo} />
      );
      title = (
        <div className="agreements-title">
          <p className="agreements-modal-header">EVOLVE BANK & TRUST</p>
          <p className="agreements-modal-subheader">
            CUSTOMER ACCOUNT AGREEMENT
          </p>
        </div>
      );
      break;
    case "crbAgreement":
      content = (
        // @ts-expect-error info is undeclared by CRBAgreement; passed as in the original
        <CRBAgreement info={termsInfo} />
      );
      title = (
        <div className="agreements-title">
          <p className="agreements-modal-header">
            CROSS RIVER BANK DEPOSIT ACCOUNT AGREEMENT
          </p>
          <p className="agreements-modal-subheader">
            Last Updated December 19, 2025
          </p>
        </div>
      );
      break;
    case "passportAgreement":
      content = (
        // @ts-expect-error PassportAgreementSila narrows info to { effectiveDate: string }; passed as in the original
        <PassportAgreementSila info={termsInfo} />
      );
      title = (
        <div className="agreements-title">
          <p className="agreements-modal-header">PASSPORT</p>
          <p className="agreements-modal-subheader">
            PASSPORT ACCOUNT AGREEMENT
          </p>
        </div>
      );
      break;
    case "employerAgreement":
      // termsInfo is a loose `object` here; the converted terms components type
      // `info` precisely. Cast at this consumer boundary (the runtime value is
      // the right shape for the shown agreement type).
      content = (
        <EmployerTermsAgreement
          info={
            termsInfo as React.ComponentProps<
              typeof EmployerTermsAgreement
            >["info"]
          }
        />
      );
      title = <div>PLATFORM SERVICES AGREEMENT</div>;
      break;
    case "employerOrder":
      content = (
        <EmployerTermsOrder
          info={
            termsInfo as React.ComponentProps<typeof EmployerTermsOrder>["info"]
          }
        />
      );
      title = <div>EXHIBIT A - ORDER FORM</div>;
      break;
    case "employerOrderV2":
      content = (
        <EmployerTermsOrderV2
          info={
            termsInfo as React.ComponentProps<
              typeof EmployerTermsOrderV2
            >["info"]
          }
        />
      );
      title = <div>EXHIBIT A - ORDER FORM</div>;
      break;
    case "isolvedOrder":
      content = (
        <EmployerIsolvedTermsOrder
          info={
            termsInfo as React.ComponentProps<
              typeof EmployerIsolvedTermsOrder
            >["info"]
          }
        />
      );
      title = <div>EXHIBIT A - ORDER FORM</div>;
      break;
    case "employerPartnerProgram":
      // EmployerPartnerProgramAgreement derives its company name from redux and
      // never reads `info`, so the original `info` pass was dead — drop it
      // (behavior-identical, avoids an otherwise-needless @ts-expect-error).
      content = <EmployerPartnerProgramAgreement />;
      title = <div>EMPLOYER REFERRAL PROGRAM AGREEMENT</div>;
      break;
    case "iraAgreement":
      content = (
        // @ts-expect-error info is undeclared by IRATerms; passed as in the original
        <IRATerms info={termsInfo} />
      );
      title = <div>New Account Application & Agreement</div>;
      break;
    case "virtualAccountNumberDisclosure":
      content = (
        // @ts-expect-error info is undeclared by VirtualAccountNumberDisclosure; passed as in the original
        <VirtualAccountNumberDisclosure info={termsInfo} />
      );
      title = (
        <div>
          General Disclosure/Disclaimer Regarding the Use of Virtual Account
          Numbers (VANs)
        </div>
      );
      break;
    default:
      content = "";
      break;
  }

  return (
    <Modal
      centered
      scrollable
      show={show}
      onHide={onClose}
      size="lg"
      className="terms-modal"
    >
      <Modal.Header style={{ alignItems: "center" }}>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{content}</Modal.Body>
      <Modal.Footer>
        <Button name="cancel" btnLabel="Close" onClick={onClose} />
      </Modal.Footer>
    </Modal>
  );
};

export default TermsModal;
